import { ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router";

import { useConnectorOverview } from "@/lib/api";
import { useLanguage } from "@/lib/i18n/language";
import { toolText, withGroupText } from "@/lib/i18n/tools";
import { servedTools, toolsByGroup } from "@/lib/tool-groups";

import { docsContent } from "./content";
import { RichText } from "./doc-parts";

/** A catalogue summary as a sentence: with one full stop at the end. */
function sentence(summary: string): string {
  return `${summary.replace(/\.+$/, "")}.`;
}

/**
 * The tools the reader gets in Claude, as the overview lists them for their
 * role (most read Odoo; the roadmap's and the submissions to Velta have
 * groups of their own): each tool the connector serves in a sentence under
 * its group, with the Tools page's group titles (from the backend's
 * catalogue, in the dashboard's language), and a link to the Tools page,
 * which says which are on and where the reader switches one off for
 * themselves. Tools that are coming soon are left out: Claude cannot use
 * them yet.
 */
export function ToolListPage() {
  const { data: overview, isLoading } = useConnectorOverview();
  const language = useLanguage();
  const text = docsContent[language].capabilities;
  const groups = overview
    ? toolsByGroup(withGroupText(overview.groups, language), servedTools(overview.tools))
    : [];
  return (
    <>
      <p>
        <RichText text={text.toolsNote} />{" "}
        <Link
          to="/tools"
          className="text-accent inline-flex items-center gap-1 font-medium hover:underline"
        >
          {text.toolsLink}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </p>

      {isLoading && (
        <p className="text-muted flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {text.toolsLoading}
        </p>
      )}
      {!isLoading && !overview && <p className="text-muted">{text.toolsFailed}</p>}
      {groups.map((group) => (
        <div key={group.id}>
          <h3 className="text-primary font-semibold">{group.title}</h3>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {group.tools.map((tool) => {
              const { title, summary } = toolText(tool, language);
              return (
                <li key={tool.name}>
                  <strong className="font-semibold">{title}</strong>
                  {text.toolSeparator}
                  {sentence(summary)}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
}
